<template>
  <div class="sections-holder">
    <FaqSection>
      <span slot="heading">
        What is the standard deviation of 5 5 9 9 9 10 5 10 10?
      </span>
      <div slot="content">
        <p>You can find the standard deviation of 5 5 9 9 9 10 5 10 10 using our Standard Deviation Calculator. But knowing how to manually calculate gives you a definite advantage when running statistical analysis. Learn how to do it by following these steps:</p>
        <p>We begin by calculating the sample’s mean value:</p>
        <p class="formulas-overflow">
          \[\bar{X}=\frac{\sum_{i=1}^{n}X_i}{n}=\frac{5+5+9+9+9+10+5+10+10}{10}=8\]
        </p>
        <p>Then, we calculate the deviations from the sample mean. Start with the first variable, subtracting it from the mean value:</p>
        <p class="formulas-overflow">
          \[(X_i-\bar{X})=5-8=-3\]
        </p>
        <p>Now, square each deviation from the sample mean. Our first variable becomes:</p>
        <p class="formulas-overflow">
          \[{(X_i-\bar{X})}^2={-3}^2=9\]
        </p>
        <p>Find the sum of the squared deviations:</p>
        <p class="formulas-overflow">
          \[\sum_{i=1}^{n}{\left(X_i-\bar{X}\right)^2=\sum_{i=1}^{n}{\left(5-8\right)^2+\left(5-8\right)^2+...+\left(10-8\right)^2}=42}\]
        </p>
        <p>Divide the sum of squared deviations by n – 1 to obtain the variance (s2)</p>
        <p class="formulas-overflow">
          \[s^2=\sum_{i=1}^{n}\frac{(X_i-\bar{X})^2}{n-1}=\frac{42}{9-1}=5.25\]
        </p>
        <p>Take the square root of the sum of the squared deviations to calculate the sample standard deviation:</p>
        <p class="formulas-overflow">
          \[s=\sqrt{\sum_{i=1}^{n}\frac{(X_i-\bar{X})^2}{n-1}}=\sqrt{5.25}=2.29\]
        </p>
        <p>So, the standard deviation of 5 5 9 9 9 10 5 10 10 is <span class="bolded-text">2.29</span>.</p>
      </div>
    </FaqSection>
    <hr>
    <FaqSection>
      <span slot="heading">
        How do we calculate standard deviation in Excel?
      </span>
      <div slot="content">
        <p>Excel has six built-in functions that allow us to calculate the standard deviation of a dataset¾ three for sample data and three for population data.</p>
        <p>We find the population standard deviation in Excel with the formula STDEV.P(number1,[number2],…). This is the newer version, available from Excel 2010 onward, that has improved accuracy for estimation. You can still use the old formula, STDEVP( number1,[number2],…), as it’s still available for backward compatibility.</p>
        <p>Meanwhile, STDEVPA(value1, [value2], …) calculates the standard deviation based on a population. Its biggest advantage over STDEVP and STDEV.P is that it includes text and logical values in the calculation; it reads all FALSE values as 0, whereas text and TRUE values are represented by 1. When dealing with numerical data, STDEVPA will return the same result as the STDEV.P function.</p>
        <p>As for the sample standard deviation, we calculate it using the Excel 2010 formula STDEV.S(number1,[number2],…). It offers better accuracy and calculates the sample standard deviation on numeric values. Similarly to the population SD, this new formula replaces the old STDEV(number1,[number2],…), which you can still find available in older Excel versions for the sake of backward compatibility.</p>
        <p>Meanwhile, STDEVA(value1, [value2], …) calculates the sample standard deviation and considers text and logical values. The function assigns 1 to TRUE arguments and 0 to FALSE ones. In this case, text values are treated as 0.</p>
        <p>Which formulas you should use depends on the type of data (sample or population; numerical or logical and text) and the version of Excel you have (pre-2010 or newer).</p>
        <p>For example, if you’re evaluating numerical sample data on Excel 2010 or a later version, then STDEV.S is the right choice. However, if your data contains text or logical values, then you should go for STDEVA. Here is a summary of all six functions:</p>
        <img src="../../../../assets/images/calculators/standard-deviation/sd-01-faq-desktop.png" alt="STDEV functions table.">
        <p>To illustrate how to calculate standard deviation in Excel, suppose you have the stock returns of Company AXY over the past 10 months. Now, you’d like to estimate their standard deviation:</p>
        <img src="../../../../assets/images/calculators/standard-deviation/sd-02-faq-desktop.png" alt="Stock returns.">
        <p>Since this is just a sample of returns and we only have numerical data, we can go for the STDEV.S formula. Type the function, select the data, and close the parentheses to obtain 11.58%:</p>
        <img src="../../../../assets/images/calculators/standard-deviation/sd-03-faq-desktop.png" alt="STDEV.S formula in Excel.">
        <p>If you use Excel 2007 or prior, you’ll have to use STDEV instead:</p>
        <img src="../../../../assets/images/calculators/standard-deviation/sd-04-faq-desktop.png" alt="STDEV formula in Excel.">
        <img class="no-top-margin" src="../../../../assets/images/calculators/standard-deviation/sd-05-faq-desktop.png" alt="STDEV.S and STDEV formula in Excel.">
        <p>The result is the same: <span class="bolded-text">11.58%</span>.</p>
      </div>
    </FaqSection>
  </div>
</template>

<script>
import FaqSection from './Section.vue';

export default {
  components: {
    FaqSection,
  },
};
</script>

<style lang="scss" scoped>
.sections-holder {
  hr {
    margin: var(--spacing-8) 0 var(--spacing-24);
    border: 1px solid rgba(0, 12, 31, 0.12);
  }
}
</style>
